import React, { useEffect, useRef, useState } from "react";
import { Saturation, Hue, Alpha, useColor } from "react-color-palette";
import "react-color-palette/css";
import { useTranslation } from "react-i18next";
import * as TablerIcons from "@tabler/icons-react";
import { ColorPickerFields } from "./ColorPickerFields";
import Button from "../button/Button";
import Dropdown from "../dropdown/Dropdown";
import { hexToHSLA, rgbToHex } from "../../../styles/helpers";
import { Dot } from "../../../assets/icons";
import Input from "../../atoms/inputs/Input";

function adjustColor(initialColor, initialOpacity) {
  if (initialColor.includes("hsl")) {
    const [h, s, l] = initialColor.match(/\d+\.?\d*/g);
    return `hsl(${h} ${s}% ${l}% / ${initialOpacity})`;
  }
  return hexToHSLA(initialColor, initialOpacity);
}

const ColorPickerItems = ({
  initialColorRef,
  color,
  setColor,
  handleSave,
  initialOpacity,
}) => {
  const { t } = useTranslation();
  const [clickCount, setClickCount] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleClick = () => {
    const opacity = color.rgb?.a;
    const r = parseInt(color.rgb?.r.toFixed(0), 10);
    const g = parseInt(color.rgb?.g.toFixed(0), 10);
    const b = parseInt(color.rgb?.b.toFixed(0), 10);
    const hexColor = rgbToHex(r, g, b);

    initialColorRef.current = color;

    handleSave(hexColor, opacity);

    setClickCount((prevCount) => (prevCount === 1 ? 0 : prevCount + 1));
  };

  useEffect(() => {
    if (clickCount === 1) {
      handleClick();
    }
  }, [clickCount]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (!isLoaded) {
    return null;
  }

  return (
    <div className="flex w-72 flex-col items-end justify-end gap-3 p-3">
      <Saturation
        height={100}
        color={color}
        onChange={setColor}
      />
      <Hue
        color={color}
        onChange={setColor}
      />
      <Alpha
        color={color}
        onChange={setColor}
      />
      <ColorPickerFields
        hideInput={false}
        color={color}
        onChange={setColor}
        handleSave={handleClick}
      />
    </div>
  );
};

const ColorPicker = ({
  initialColor = "#561ecb",
  initialOpacity = 1,
  handleSave,
}: {
  initialColor: string;
  initialOpacity: number;
  handleSave: (color: string, opacity: number) => Promise<void>;
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [color, setColor] = useColor(adjustColor(initialColor, initialOpacity));

  const initialColorRef = useRef(color);
  const handleSaveColor = async (color: string, opacity: number) => {
    try {
      await handleSave(color, opacity);
      setDropdownOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dropdown
      size="sm"
      open={dropdownOpen}
      setOpen={(val) => {
        if (val === false) {
          setTimeout(() => {
            setColor(initialColorRef.current);
          }, 100);
        }

        setDropdownOpen(val);
      }}
      leadingIcon={
        <Dot
          color={color.hex}
          style={{ opacity: initialOpacity }}
        />
      }
      trailingIcon={undefined}
      items={[
        {
          children: (
            <div className="flex">
              <ColorPickerItems
                initialColorRef={initialColorRef}
                color={color}
                setColor={setColor}
                handleSave={handleSaveColor}
                initialOpacity={initialOpacity}
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default ColorPicker;
// End of Selection
